import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { isColumnHidden } from 'src/utils/form/dataGrid';
import Checkbox from 'src/components/Checkbox';
import TextField from 'src/components/TextField';
import CurrentUserContext from 'src/utils/context/CurrentUserContext';
import { DEFAULT_META_QUERY } from 'src/graphql/defaultMeta/query';
import { EDIT_DEFAULT_META_MUTATION } from 'src/graphql/defaultMeta/mutation';
import client from 'src/utils/api/client';
import { notification } from 'src/components/Notification';
import { useTranslation } from 'react-i18next';
import s from './VisibilitySettings.styles';

const VisibilitySettingsPopup = props => {
  const { columns, meta, onSubmit, onClose } = props;

  const ref = useRef(null);
  const { t } = useTranslation();

  const user = useContext(CurrentUserContext);
  const isAdmin = !!user.roles.find(c => c.name === 'Admin');

  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    const newData = [];
    columns.forEach((name, index) => {
      newData.push({
        name,
        checked: !isColumnHidden(meta, index),
      });
    });
    setData(newData);
  }, []);

  const changeData = (index, checked) => {
    const newData = [...data];
    newData[index].checked = checked;
    setData(newData);
  };

  const preparedData = () => {
    let result = 0;

    data.forEach(({ checked }, index) => {
      if (checked) {
        // eslint-disable-next-line no-bitwise
        result |= 1 << index;
      }
    });

    if (result === 0) {
      result = 1;
    }

    return result;
  };

  const restoreDefaults = () => {
    client
      .query({
        query: DEFAULT_META_QUERY,
        variables: {
          key: props.name,
        },
        fetchPolicy: 'network-only',
      })
      .then(
        response => {
          const { value } = response.data.defaultMeta;

          const newData = [];
          columns.forEach((name, index) => {
            newData.push({
              name,
              checked: !isColumnHidden(value, index),
            });
          });
          setData(newData);
        },
        () => {
          const newData = [];
          columns.forEach(name => {
            newData.push({
              name,
              checked: true,
            });
          });
          setData(newData);
        },
      );
  };

  const submitDefaults = (key, value) => {
    client
      .mutate({
        mutation: EDIT_DEFAULT_META_MUTATION,
        variables: {
          key,
          value,
        },
      })
      .then(() => {
        notification.success(t('DEFAULT_WAS_CHANGED'));
      });
  };

  return (
    <s.SettingsPopup ref={ref}>
      <s.SettingSearch>
        <TextField
          onChange={e => setSearch(e.target.value)}
          placeholder={t('SEARCH_COLUMN')}
          value={search}
        />
        <s.SettingSearchIcon name="search" />
      </s.SettingSearch>
      <s.Button link onClick={() => restoreDefaults()} sm>
        {t('RESTORE_TO_DEFAULT')}
      </s.Button>
      <s.SettingsContent>
        {data.map(({ name, checked }, index) => {
          if (
            search &&
            name.toLowerCase().indexOf(search.toLowerCase()) === -1
          ) {
            return null;
          }

          return (
            <Checkbox
              key={index}
              label={name}
              onChange={e => changeData(index, e.target.checked)}
              value={checked}
            />
          );
        })}
      </s.SettingsContent>
      <s.SettingsFooter>
        <s.Button onClick={() => onSubmit(preparedData())} orange sm solid>
          {t('SAVE')}
        </s.Button>
        <s.Button ghost onClick={onClose} red sm>
          {t('CANCEL')}
        </s.Button>
        {isAdmin && (
          <s.AdminWrapper>
            <s.Button
              link
              onClick={() =>
                submitDefaults(props.name, preparedData().toString())
              }
              sm
            >
              {t('SET_AS_DEFAULT')}
            </s.Button>
          </s.AdminWrapper>
        )}
      </s.SettingsFooter>
    </s.SettingsPopup>
  );
};

VisibilitySettingsPopup.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string,
  meta: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default React.memo(VisibilitySettingsPopup);
